import SimpleTextSection from "../simpleText"
import CustomButton from "../../common/button"
import heartImg from "public/images/hearts/Half-heart-element.png"
import heartTrustImg from "public/images/hearts/Build-trust-half-heart.png"
import type { SectionProps } from "../../common/section"
import { ContactButton } from "../../pageSpecific/contact/contactButton"
import iconImg from "public/images/logo/Incluso Services Logogram-Icon.svg"
import Image from "next/image"
import { ArrowLongRightIcon } from "@heroicons/react/20/solid"
import TextLink from "../../common/textLink"
import { type SimpleTextProps } from "../simpleText"
import { PriceGuide, Referral } from "~/content/navLinks"

export interface SimpleTextSectionProps extends SimpleTextProps {
  link?: boolean
}
export const NetZeroTextSection = ({ link }: SimpleTextSectionProps) => {
  return (
    <SimpleTextSection
      theme="light"
      title={
        <>
          <div className="mb-4 flex gap-0.5">
            <video autoPlay muted loop playsInline className="h-stretch w-20 xs:w-24 lg:w-28 xl:w-32">
              <source src="/images/animations/netzero-520x968-incluso.mp4" type="video/mp4" />
            </video>
            <div className="flex flex-col justify-center bg-green px-6 text-left font-bold text-white">
              <p className="text-3xl md:text-4xl lg:text-5xl">100%</p>
              <p className="text-2xl md:text-3xl lg:text-4xl">Carbon Neutral</p>
            </div>
          </div>
        </>
      }
      body={
        <>
          <div>
            <p className="">We are passionate about ensuring our beautiful planet is here for generations to come.</p>
            <p className="mt-1 text-[1.2em]/6 font-bold sm:mt-1.5 sm:text-[1.4rem]/6 lg:mt-0 lg:text-2xl/7">
              100% Carbon Neutral
              <span className="block">20% annual waste reduction targets</span>
            </p>
          </div>
        </>
      }
      bodyWidth="w-full md:w-[42rem] lg:w-[47rem]"
      afterAction={
        link ? (
          <div className="leading-3">
            <TextLink
              href="/about-us/community-and-sustainability/environmental"
              className="text-desk-resp-xl font-bold text-green"
              hoverVariant="background"
              hoverColor="bg-green"
            >
              Learn more about how we are creating a sustainable future
              <ArrowLongRightIcon className="mb-0.5 ml-1 inline size-5 lg:size-6" />
            </TextLink>
          </div>
        ) : undefined
      }
      multiLine
    />
  )
}

export const CommunityTextSection = ({ customBorder }: SimpleTextProps) => {
  return (
    <SimpleTextSection
      bgClass={`bg-[url('/images/Forest.png')] bg-no-repeat bg-center bg-cover ${customBorder}`}
      title={
        // <h2 className="text-desk-resp-3xl mb-4 text-white lg:mb-6 xl:mb-8">
        //   We don’t just talk about Community and Sustainability
        // </h2>
        <h2 className="text-desk-resp-3xl text-white max-w-xl">
          {/* We don’t just talk about Community and Sustainability */}
          We’re only just getting started, but together we can shape a better tomorrow.
        </h2>
      }
      // body={
      //   <>
      //     <p className="leading-6 text-white xl:text-2xl/7">
      //       At Incluso, we are a team of people that come together to do our
      //       best to make sure that we leave our beautiful planet better tomorrow
      //       than we found it today. Help to be part of the solution.
      //     </p>
      //   </>
      // }
      // bodyWidth="w-full lg:w-[45rem] xl:w-[55rem]"
      // multiLine
    />
  )
}

export const AdamQuoteTextSection = ({ body, customPadding }: SimpleTextSectionProps) => {
  return (
    <SimpleTextSection
      theme="light"
      title={<Image src={iconImg} alt="incluso-logo" className="mb-2 size-20 lg:mb-4 lg:size-24 xl:size-28" />}
      body={
        <>
          {body}
          <div className="text-purpleblue">
            <p className="font-bold">Adam Centorrino</p>
            <p>Founder, Incluso Services</p>
          </div>
        </>
      }
      bodyWidth="w-full sm:w-4/5 xl:w-[52rem]"
      padding="md"
      multiLine
      customPadding={customPadding}
      flexHeight
    />
  )
}

export const ConciergeTextSection = () => {
  return (
    <SimpleTextSection
      image={heartImg}
      theme="dark"
      body={
        <>
          <p>Ever heard another provider having a concierge service? </p>
          <strong className="text-orange">We told you we’re different.</strong>
        </>
      }
      lineGap="lg:gap-2"
    />
  )
}

export const TechnologyTextSection = () => {
  return (
    <SimpleTextSection
      theme="dark"
      image={heartImg}
      hideImageOnMobile
      body={
        <>
          <p className="">
            We’re challenging the status quo by moving away from the tiresome traditional paper-based methods and{" "}
            <strong className="text-orange">leveraging technology</strong> to enhance efficiency, accuracy, and education.{" "}
          </p>
        </>
      }
    />
  )
}

export const BookServiceTextSection = () => {
  return (
    <SimpleTextSection
      theme="light"
      flexHeight
      title="Not sure what you need?"
      titleColour="blue"
      body={
        <>
          <p>
            Book time with our expert team to discuss your needs, more detailed information about the NDIS, including eligibility criteria, how to apply, and
            the types of support available.
          </p>
          <p>Together we’ll work out the best plan and support for you.</p>
        </>
      }
      multiLine
      afterAction={
        <ContactButton size="sm" className="mx-auto w-full max-w-[20rem] py-0.5 text-lg xs:w-3/5 md:max-w-[26rem] lg:py-1.5 lg:text-xl" section="book">
          Book a discovery call
        </ContactButton>
      }
      afterActionFull
      padding="sm"
      bodyWidth="sm:w-[32rem] md:w-[40rem] lg:w-[48rem]"
    />
  )
}

export type PriceGuideProps = {
  pretitle?: boolean
  href?: string
  buttonText?: string
  newTab?: boolean
}

export const PriceGuideTextSection = ({ pretitle, href, buttonText, newTab }: PriceGuideProps) => {
  return (
    <SimpleTextSection
      theme="dark"
      body={
        <>
          <div>
            {pretitle && <h3 className="font-bold text-green">Price Guide</h3>}
            <p>
              We believe in <strong>transparency</strong> and providing our clients with pricing under the{" "}
              <strong className="block xs:inline">NDIS Price Guide.</strong>
            </p>
          </div>
          <p>Our pricing is designed to offer you the best value to ensure the funding in your plan goes further.</p>
        </>
      }
      bodyWidth="w-[19.5rem] xs:w-[25rem] sm:w-[30rem] md:w-[36rem] lg:w-[50rem]"
      image={heartTrustImg}
      imgPosition="-right-10 md:-right-12 lg:-right-14 xl:-right-[4rem]"
      afterAction={
        <CustomButton
          size="sm"
          href={href ?? PriceGuide.href}
          newTab={newTab ? true : false}
          className={`mb-3 xs:mb-4 ${pretitle ? "px-6 text-xs xs:text-sm sm:text-base md:min-w-[24rem] lg:text-xl" : "sm:w-[18rem] lg:w-[24rem] lg:text-lg"}`}
        >
          {buttonText ?? "Download Pricing Guidelines"}
        </CustomButton>
      }
      padding="md"
    />
  )
}

export const ReferralTextSection = ({ flexHeight }: SectionProps) => {
  return (
    <SimpleTextSection
      theme="light"
      title={<h2 className="mb-4 text-[1.7rem] xs:pt-4 sm:text-4xl md:pt-6 lg:mb-6 lg:pt-8 lg:text-[2.6rem] xl:mb-8">Become a Referral Partner</h2>}
      image={heartImg}
      imgLeft
      body={
        <p className="md:text-base lg:text-lg">
          We love to collaborate, to become a Referral Partner with us, all you have to do is contact us, and we will arrange the rest!
        </p>
      }
      bodyWidth="xs:w-[27rem] lg:w-[30rem]"
      afterAction={
        <CustomButton 
        size="sm" 
        // href="http://45622326.hs-sites.com/provider-registration-form" 
        href={Referral.href}
        // newTab 
        className="w-80 md:w-[24rem] lg:w-[26rem] lg:text-lg">
          Collaborate with us
        </CustomButton>
      }
      flexHeight={flexHeight ?? true}
      padding="sm"
      multiLine
    />
  )
}

export const ExploreWorkTextSection = ({ customBorder }: SimpleTextSectionProps) => {
  return (
    <SimpleTextSection
      theme="dark"
      title="Explore more about how we work"
      titleColour="text-orange"
      body={
        <>
          <p className="lg:text-2xl xl:text-3xl/10">We are a human-centric organisation that is passionate about our customers and being great citizens.</p>
          <p className="lg:text-2xl xl:text-3xl/10">People are our most important assets and we are proud of our team members and the work that they do.</p>
          <div className="mx-auto mt-2 grid w-[17rem] gap-3 xs:w-2/3 md:w-full md:grid-cols-3 lg:px-4">
            <CustomButton href="/services" variant="outline" className="rounded-lg py-2.5 font-bold hover:bg-green/20 lg:text-xl xl:text-2xl">
              Our Service
            </CustomButton>
            <CustomButton href="/about-us/career" variant="outline" className="rounded-lg border-orange font-bold hover:bg-orange/20 lg:text-xl xl:text-2xl">
              Join the Team
            </CustomButton>
            <CustomButton href="/about-us/customer-experience" variant="outline" className="rounded-lg font-bold hover:bg-green/20 lg:text-xl xl:text-2xl">
              Customer Experience
            </CustomButton>
          </div>
        </>
      }
      bodyWidth="max-w-[21rem] xs:max-w-none xs:w-[28rem] sm:w-[30rem] md:w-[36rem] lg:w-full lg:max-w-[46rem] xl:max-w-[56rem]"
      multiLine
      image={heartImg}
      customBorder={customBorder}
    />
  )
}
